import { useMemo } from 'react';
import { DashBoardValue } from 'features/dashBoard/hooks'
import { DashBoardItemOption } from 'features/dashBoard/types'
import { GraphOptions, getAxisDef, getValueDef } from '../../common'
import { zeroPadding } from 'shared/util'

import {
  Functions as TotalIcon
} from '@mui/icons-material'

import { TargetListResult } from "interface/search/targetList"

const WeekDays = [ '月', '火', '水', '木', '金', '土', '日' ]
const WeekDaysLong = [ '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日' ]

const Hours = Array(24).fill(0).map((_, h) => zeroPadding(h, 2))
const HoursLong = Array(24).fill(0).map((_, h) => `${h}時`)

const Hours3 = Array(24 / 3).fill(0).map((_, h3) => `${zeroPadding(h3 * 3, 2)}:00 - ${zeroPadding((h3 * 3) + 2, 2)}:59`)
const Hours3Long = Array(24 / 3).fill(0).map((_, h) => `${h * 3}:00～${(h * 3) + 2}:59`)

const Months = Array(12).fill(0).map((_, m) => `${m+1}月`)
const Dates = Array(31).fill(0).map((_, d) => `${d+1}日`)

type TimeOperation = {
  label: string
  value: (time: Date) => { x: number, y: number }
  axisLabels: (minTime: Date, maxTime: Date) => { x: string[], y: string[] }
  toolTipLabel: { x: string[], y: string[] }
  condition: (data: number[]) => string
}
export const TimeOperations: { [index: string]: TimeOperation } = {
	t: {
    label: '時間',
    value: (time) => ({ x: time.getHours(), y: 0 }),
    axisLabels: () => ({ x: Hours, y: [''] }),
    toolTipLabel: { x: HoursLong, y: [''] },
    condition: (data: number[]) => `hour:${data[0]}`
	},
	t3: {
    label: '3時間',
    value: (time) => ({ x: Math.floor(time.getHours() / 3), y: 0 }),
    axisLabels: () => ({ x: Hours3, y: [''] }),
    toolTipLabel: { x: Hours3Long, y: [''] },
    condition: (data: number[]) => `hour3:${data[0]}`
	},
	w: {
    label: '曜日',
    value: (time) => ({ x: time.getDay(), y: 0 }),
    axisLabels: () => ({ x: WeekDays, y: [''] }),
    toolTipLabel: { x: WeekDaysLong, y: [''] },
    condition: (data: number[]) => `wday:${data[0]}`
	},
	d: {
    label: '日',
    value: (time) => ({ x: time.getDate(), y: 0 }),
    axisLabels: () => ({ x: Dates, y: [''] }),
    toolTipLabel: { x: Dates, y: [''] },
    condition: (data: number[]) => `date:${data[0]}`
	},
	m: {
    label: '月',
    value: (time) => ({ x: time.getMonth(), y: 0 }),
    axisLabels: () => ({ x: Months, y: [''] }),
    toolTipLabel: { x: Months, y: [''] },
    condition: (data: number[]) => `month:${data[0]}`
	},
	wt: {
    label: '曜日/時間',
    value: (time) => ({ x: time.getHours(), y: time.getDay() }),
    axisLabels: () => ({ x: Hours, y: WeekDays }),
    toolTipLabel: { x: HoursLong, y: WeekDaysLong },
    condition: (data: number[]) => `hour:${data[0]},wday:${data[1]}`
	},
	wt3: {
    label: '曜日/3時間',
    value: (time) => ({ x: Math.floor(time.getHours() / 3), y: time.getDay() }),
    axisLabels: () => ({ x: Hours3, y: WeekDays }),
    toolTipLabel: { x: Hours3Long, y: WeekDaysLong },
    condition: (data: number[]) => `hour3:${data[0]},wday:${data[1]}`
	},
  dt: {
    label: '日/時間',
    value: (time) => ({ x: time.getHours(), y: time.getDate() }),
    axisLabels: () => ({ x: Hours, y: Dates }),
    toolTipLabel: { x: HoursLong, y: Dates },
    condition: (data: number[]) => `hour:${data[0]},date:${data[1]}`
  },
	wm: {
    label: '曜日/月',
    value: (time) => ({ x: time.getMonth(), y: time.getDay() }),
    axisLabels: () => ({ x: Months, y: WeekDays }),
    toolTipLabel: { x: Months, y: WeekDaysLong },
    condition: (data: number[]) => `month:${data[0]},wday:${data[1]}`
	},
	mt: {
    label: '月/時間',
    value: (time) => ({ x: time.getHours(), y: time.getMonth() }),
    axisLabels: () => ({ x: Hours, y: Months }),
    toolTipLabel: { x: HoursLong, y: Months },
    condition: (data: number[]) => `hour:${data[0]},month:${data[1]}`
	},
	mt3: {
    label: '月/3時間',
    value: (time) => ({ x: Math.floor(time.getHours() / 3), y: time.getMonth() }),
    axisLabels: () => ({ x: Hours3, y: Months }),
    toolTipLabel: { x: Hours3Long, y: Months },
    condition: (data: number[]) => `hour3:${data[0]},month:${data[1]}`
	}
}

export const HeatMapGraphOptions: GraphOptions[][] = [
  [
    {
      id: 'time',
      type: 'time',
      label: '日時'
    },
    {
      id: 'by',
      type: 'choice',
      label: '集計単位',
      prefix: 'の',
      data: Object.keys(TimeOperations).map((key) => ({
        label: TimeOperations[key].label,
        value: key
      })),
      default: 'wt'
    },
    {
      id: 'value',
      type: 'value',
      prefix: 'ごとの',
      label: '値'
    },
    {
      id: 'total',
      type: 'check',
      label: '全体',
      default: 'false',
      fromSubSource: true,
      toolBtn: <TotalIcon fontSize="small"/>
    }
  ]
]

type useHeatMapGraphOptionProps<T> = {
  dashBoard: DashBoardValue<T>
  options?: DashBoardItemOption[]
  targetList?: TargetListResult
}
export const useHeatMapGraphOption = <T,>({
  dashBoard: {
    configure
  },
  options,
  targetList
}: useHeatMapGraphOptionProps<T>) => useMemo(
  () => ({
    configure, options, targetList,
    timeDef: getAxisDef(configure, options, 'time'),
		by: options?.find((opt) => opt.id === 'by')?.value || 'wt',
		valueDef: getValueDef(configure, options, 'value', targetList),
    total: options?.find((opt) => opt.id === 'total')?.value === 'true'
	}),
  [options, configure, targetList]
)
